import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, CheckCircle, XCircle, Loader2, FileText, Package, Clock, User, AlertTriangle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import ProcurementService from '@/api/procurement.service';
import { canEdit } from '@/lib/permissions';

const PurchaseRequisitionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [requisition, setRequisition] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    if (id) {
      fetchRequisition();
    }
  }, [id]);

  const fetchRequisition = async () => {
    try {
      setIsLoading(true);
      const response = await ProcurementService.getPurchaseRequisition(id!);
      
      if (response.data?.success) {
        setRequisition(response.data.data);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load purchase requisition",
        });
        navigate('/procurement/requisitions');
      }
    } catch (error: any) {
      console.error('Error fetching purchase requisition:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load purchase requisition",
      });
      navigate('/procurement/requisitions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string, comment?: string) => {
    setIsUpdating(true);
    try {
      const response = await ProcurementService.updatePurchaseRequisition(id!, { status: newStatus, comment });
      
      if (response.data?.success) {
        toast({
          title: "Success",
          description: `Purchase requisition ${newStatus.replace('_', ' ')} successfully`,
        });
        fetchRequisition(); // Refresh data
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data?.message || "Failed to update status",
        });
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update status",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { variant: 'secondary' as const, label: 'Draft' },
      pending_approval: { variant: 'outline' as const, label: 'Pending Approval' },
      approved: { variant: 'default' as const, label: 'Approved' },
      rejected: { variant: 'destructive' as const, label: 'Rejected' },
      cancelled: { variant: 'destructive' as const, label: 'Cancelled' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'secondary' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: 'secondary' as const, label: 'Low' },
      medium: { variant: 'outline' as const, label: 'Medium' },
      high: { variant: 'default' as const, label: 'High' },
      urgent: { variant: 'destructive' as const, label: 'Urgent' }
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || { variant: 'secondary' as const, label: priority };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getUrgencyBadge = (urgency: string) => {
    const urgencyConfig = {
      low: { variant: 'secondary' as const, label: 'Low' },
      medium: { variant: 'outline' as const, label: 'Medium' },
      high: { variant: 'default' as const, label: 'High' },
      urgent: { variant: 'destructive' as const, label: 'Urgent' }
    };

    const config = urgencyConfig[urgency as keyof typeof urgencyConfig] || { variant: 'secondary' as const, label: urgency };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading purchase requisition...</span>
        </div>
      </div>
    );
  }

  if (!requisition) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Purchase Requisition Not Found</h3>
          <p className="text-muted-foreground">The requested purchase requisition could not be found.</p>
          <Button 
            onClick={() => navigate('/procurement/requisitions')}
            className="mt-4"
          >
            Back to Purchase Requisitions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/procurement/requisitions')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Purchase Requisitions
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {canEdit('purchase_requisitions') && (requisition.status === 'draft' || requisition.status === 'rejected') && (
            <Button
              onClick={() => navigate(`/procurement/requisitions/${id}/edit`)}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
          
          {canEdit('Procurement') && requisition.status === 'pending_approval' && (
            <>
              <Button
                onClick={() => handleStatusUpdate('approved', 'Approved via system')}
                disabled={isUpdating}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Approve
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleStatusUpdate('rejected', 'Rejected via system')}
                disabled={isUpdating}
                className="flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Requisition Header */}
      <div>
        <div className="flex items-center gap-4 mb-2">
          <h1 className="text-3xl font-bold tracking-tight">Purchase Requisition #{requisition.requisitionNumber}</h1>
          {getStatusBadge(requisition.status)}
          {getPriorityBadge(requisition.priority)}
        </div>
        <p className="text-muted-foreground">
          Requested on {new Date(requisition.requestDate).toLocaleDateString()} by {requisition.requestedBy}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estimated Total</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(requisition.estimatedTotalCost || 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{requisition.items?.length || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Required Date</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {new Date(requisition.requiredDate).toLocaleDateString()}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Department</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">{requisition.department}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="approval">Approval History</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Requisition Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Requisition Number</label>
                    <p className="text-sm">{requisition.requisitionNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Request Date</label>
                    <p className="text-sm">{new Date(requisition.requestDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Required Date</label>
                    <p className="text-sm">{new Date(requisition.requiredDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Department</label>
                    <p className="text-sm">{requisition.department}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Requested By</label>
                    <p className="text-sm">{requisition.requestedBy}</p>
                  </div>
                  {requisition.budgetCode && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Budget Code</label>
                      <p className="text-sm">{requisition.budgetCode}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Justification</label>
                  <p className="text-sm">{requisition.justification}</p>
                </div>
                {requisition.notes && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Additional Notes</label>
                    <p className="text-sm">{requisition.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Status Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Current Status</label>
                  <div className="mt-1">{getStatusBadge(requisition.status)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Priority</label>
                  <div className="mt-1">{getPriorityBadge(requisition.priority)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Estimated Total Cost</label>
                  <p className="text-lg font-semibold">{formatCurrency(requisition.estimatedTotalCost)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created</label>
                  <p className="text-sm">{new Date(requisition.createdAt || requisition.requestDate).toLocaleString()}</p>
                </div>
                {requisition.updatedAt && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                    <p className="text-sm">{new Date(requisition.updatedAt).toLocaleString()}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Requested Items</CardTitle>
              <CardDescription>Items included in this purchase requisition</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Est. Unit Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Urgency</TableHead>
                    <TableHead>Justification</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requisition.items?.map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.itemName || item.name}</TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.estimatedUnitPrice)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.quantity * item.estimatedUnitPrice)}</TableCell>
                      <TableCell>{getUrgencyBadge(item.urgency)}</TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm truncate" title={item.justification}>
                          {item.justification}
                        </p>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="mt-4 space-y-2 border-t pt-4">
                <div className="flex justify-between font-bold">
                  <span>Estimated Total:</span>
                  <span>{formatCurrency(requisition.estimatedTotalCost || 0)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approval" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Approval History</CardTitle>
              <CardDescription>Track the approval progress of this requisition</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {requisition.approvalHistory?.map((entry: any, index: number) => (
                  <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                    <div className="flex-shrink-0">
                      {getStatusBadge(entry.status)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{entry.comment || `Status changed to ${entry.status}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.createdAt).toLocaleString()} by {entry.user?.name || entry.approver || 'System'}
                      </p>
                    </div>
                  </div>
                )) || (
                  <p className="text-muted-foreground">No approval history available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Related Documents</CardTitle>
              <CardDescription>Documents associated with this purchase requisition</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No documents available</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PurchaseRequisitionDetail; 